import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Users, Moon, Settings2, Plus, Rss, RefreshCw, ExternalLink } from "lucide-react";
import { CHANNEL_COLORS } from "@/lib/channels";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import PropertiesManager from "@/components/PropertiesManager";
import { TaskDialog, TaskChip } from "@/components/TaskDialog";
import { useCompany } from "@/context/CompanyContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LETTERS = ["S","M","T","W","T","F","S"];

function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function toISODate(y, m, d) {
  const mm = String(m + 1).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

export default function CalendarView() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskInitial, setTaskInitial] = useState(null);
  const { company } = useCompany();
  const [feeds, setFeeds] = useState([]); // list of {property, channel, url}
  const [icalEvents, setIcalEvents] = useState([]);
  const [icalErrors, setIcalErrors] = useState([]);
  const [icalLoading, setIcalLoading] = useState(false);
  const [icalSavedAt, setIcalSavedAt] = useState(null);

  const loadFeeds = async () => {
    try {
      const r = await api.get("/ical/feeds");
      setFeeds(Array.isArray(r.data) ? r.data : []);
    } catch {}
  };

  const addFeedRow = () => setFeeds((f) => [...f, { property: "", channel: "Airbnb", url: "" }]);
  const updateFeedRow = (i, k, v) => setFeeds((f) => f.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeFeedRow = (i) => setFeeds((f) => f.filter((_, idx) => idx !== i));

  const loadCachedEvents = async () => {
    try {
      const r = await api.get("/ical/events");
      setIcalEvents(r.data.events || []);
      setIcalErrors(r.data.errors || []);
      if (r.data.synced_at) setIcalSavedAt(new Date(r.data.synced_at).toLocaleString("en-GB"));
    } catch {}
  };

  const syncFeeds = async (force = true) => {
    setIcalLoading(true);
    try {
      await api.put("/ical/feeds", { entries: feeds });
      const r = await api.get("/ical/events", { params: { force } });
      setIcalEvents(r.data.events || []);
      setIcalErrors(r.data.errors || []);
      if (r.data.synced_at) setIcalSavedAt(new Date(r.data.synced_at).toLocaleString("en-GB"));
      const errCount = (r.data.errors || []).length;
      if (errCount > 0) toast.warning(`Synced with ${errCount} feed error${errCount === 1 ? "" : "s"}`);
      else if (force) toast.success(`Synced ${r.data.events?.length || 0} events`);
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setIcalLoading(false);
    }
  };

  useEffect(() => {
    loadFeeds();
    loadCachedEvents();
  }, [company]);

  // Real-time auto-refresh every 60 s while on Calendar page
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/ical/events").then((r) => {
        setIcalEvents(r.data.events || []);
        setIcalErrors(r.data.errors || []);
        if (r.data.synced_at) setIcalSavedAt(new Date(r.data.synced_at).toLocaleString("en-GB"));
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [company]);

  const load = () => {
    setLoading(true);
    // Fetch ALL bookings for the active company so past + spanning bookings render on every month
    api
      .get("/bookings", { params: { limit: 2000 } })
      .then((r) => setBookings(r.data))
      .finally(() => setLoading(false));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/tasks").then((r) => setTasks(r.data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [company]);

  const days = daysInMonth(year, month);
  const dayList = Array.from({ length: days }, (_, i) => i + 1);
  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);

  // Build per-property structured bookings within this month
  // - Exclude VACANT (not real bookings)
  // - Include CANCELLED (kept for record tracking; styled differently, no conflict)
  const grid = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      if (!b.property_booked || !b.check_in) continue;
      const st = (b.status || "").toUpperCase();
      if (st === "VACANT") continue;
      const cancelled = st === "CANCELLED";
      const ci = new Date(b.check_in);
      const co = b.check_out ? new Date(b.check_out) : ci;
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month, days);
      const start = ci < monthStart ? monthStart : ci;
      const end = co > monthEnd ? monthEnd : co;
      if (end < monthStart || start > monthEnd) continue;
      const startDay = start.getDate();
      const endDay = end.getDate();
      const totalNights = Math.max(1, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
      if (!map.has(b.property_booked)) map.set(b.property_booked, []);
      map.get(b.property_booked).push({ b, startDay, endDay, totalNights, cancelled });
    }
    return map;
  }, [bookings, year, month, days]);

  const conflicts = useMemo(() => {
    const c = new Set();
    for (const [prop, items] of grid.entries()) {
      for (let d = 1; d <= days; d++) {
        const cellDate = toISODate(year, month, d);
        // Cancelled bookings never cause conflict. Half-open interval [check_in, check_out):
        // same-day turnover (A check_out == B check_in) does NOT count as conflict.
        const overlapping = items.filter((x) => {
          if (x.cancelled) return false;
          const ci = x.b.check_in;
          const co = x.b.check_out || ci;
          if (ci === co) return cellDate === ci;
          return cellDate >= ci && cellDate < co;
        });
        if (overlapping.length > 1) c.add(`${prop}|${d}`);
      }
    }
    return c;
  }, [grid, days, year, month]);

  // Assign a vertical lane per booking per property so overlapping stays stack (never overlap visually)
  const lanes = useMemo(() => {
    const map = new Map(); // property → { bookingId: laneIdx, maxLanes: n }
    for (const [prop, items] of grid.entries()) {
      const seen = new Set();
      const unique = items
        .filter((x) => (seen.has(x.b.id) ? false : (seen.add(x.b.id), true)))
        .sort((a, b) => (a.b.check_in || "").localeCompare(b.b.check_in || ""));
      const laneEnds = []; // laneEnds[i] = check_out iso of last booking in lane i (half-open)
      const bookingLane = new Map();
      for (const x of unique) {
        const ci = x.b.check_in;
        const co = x.b.check_out || ci;
        let idx = -1;
        for (let i = 0; i < laneEnds.length; i++) {
          if (laneEnds[i] <= ci) { idx = i; break; }
        }
        if (idx === -1) { idx = laneEnds.length; laneEnds.push(""); }
        laneEnds[idx] = co;
        bookingLane.set(x.b.id, idx);
      }
      map.set(prop, { bookingLane, maxLanes: Math.max(1, laneEnds.length) });
    }
    return map;
  }, [grid]);

  const propertiesInView = properties.length ? properties : Array.from(grid.keys());
  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const jumpToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const totalBookings = bookings.filter((b) => (b.status || "").toUpperCase() !== "CANCELLED").length;
  const uniqueProps = new Set(bookings.map((b) => b.property_booked)).size;
  const doubleBooked = new Set([...conflicts].map((c) => c.split("|")[0])).size;
  const totalNights = Array.from(grid.values()).flat().reduce((sum, x) => sum + (x.endDay - x.startDay + 1), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-8 max-w-[1900px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Availability</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">Calendar</h1>
          <p className="text-sm text-slate-500 mt-2">
            Every property, every day. Bars show <span className="font-semibold">guest name · nights</span>. Red = double-booking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={prevMonth} className="rounded-sm border-slate-300 h-10 w-10 p-0" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-heading text-xl font-semibold px-4 min-w-[220px] text-center" data-testid="cal-month-label">
            {MONTHS[month]} {year}
          </div>
          <Button variant="outline" onClick={nextMonth} className="rounded-sm border-slate-300 h-10 w-10 p-0" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={jumpToday} className="rounded-sm border-slate-300 h-10" data-testid="cal-today">
            <CalendarDays className="w-4 h-4 mr-2" /> Today
          </Button>
          <Button
            onClick={() => { setTaskInitial({ kind: "CLEANING", start_date: toISODate(year, month, Math.min(today.getDate(), days)), end_date: toISODate(year, month, Math.min(today.getDate(), days)), status: "PENDING" }); setTaskOpen(true); }}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white h-10"
            data-testid="cal-new-task"
          >
            <Plus className="w-4 h-4 mr-2" /> Task
          </Button>
          <Button variant="outline" onClick={() => setManagerOpen(true)} className="rounded-sm border-slate-300 h-10" data-testid="cal-manage-props">
            <Settings2 className="w-4 h-4 mr-2" /> Properties
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <SummaryStat label="Active stays" value={totalBookings} testid="cal-total" />
        <SummaryStat label="Booked nights" value={totalNights} testid="cal-nights" />
        <SummaryStat label="Properties active" value={uniqueProps} testid="cal-properties" />
        <SummaryStat label="With conflict" value={doubleBooked} testid="cal-conflicts" highlight={doubleBooked > 0} />
      </div>

      {/* Grid */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="table-scroll overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-0" style={{ minWidth: `${260 + days * 56}px` }}>
            <thead>
              {/* Day-of-week row */}
              <tr className="bg-slate-50">
                <th className="sticky left-0 z-20 bg-slate-50 border-b border-r border-slate-200 min-w-[240px] px-3 py-2 text-left label-eyebrow">
                  Property
                </th>
                {dayList.map((d) => {
                  const dow = new Date(year, month, d).getDay();
                  return (
                    <th key={`dow-${d}`} className="border-b border-r border-slate-200 text-[10px] font-semibold text-slate-500 py-1 min-w-[56px] text-center">
                      {DAY_LETTERS[dow]}
                    </th>
                  );
                })}
              </tr>
              {/* Day number row */}
              <tr className="bg-slate-100">
                <th className="sticky left-0 z-20 bg-slate-100 border-b border-r border-slate-200 px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  {propertiesInView.length} listed
                </th>
                {dayList.map((d) => {
                  const iso = toISODate(year, month, d);
                  const isToday = iso === todayISO;
                  const dow = new Date(year, month, d).getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <th
                      key={d}
                      className={`border-b border-r border-slate-200 font-semibold min-w-[56px] py-2 text-sm text-center ${
                        isToday ? "bg-blue-900 text-white" : isWeekend ? "bg-slate-200 text-slate-700" : "text-slate-700"
                      }`}
                    >
                      {d}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={days + 1} className="p-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && propertiesInView.length === 0 && (
                <tr><td colSpan={days + 1} className="p-8 text-center text-slate-500">No properties on record.</td></tr>
              )}
              {propertiesInView.map((prop) => {
                const items = grid.get(prop) || [];
                // Deduplicate by booking id
                const seen = new Set();
                const uniqueItems = items.filter((x) => (seen.has(x.b.id) ? false : (seen.add(x.b.id), true)));
                const laneInfo = lanes.get(prop) || { bookingLane: new Map(), maxLanes: 1 };
                const rowH = Math.max(64, laneInfo.maxLanes * 26 + 12);
                return (
                  <tr key={prop} className="hover:bg-slate-50/60 transition-colors" data-testid={`cal-row-${prop}`}>
                    <td className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 px-3 py-2 font-medium truncate max-w-[240px]" title={prop} style={{ height: `${rowH}px` }}>
                      <div className="text-sm text-slate-900 truncate">{prop}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{uniqueItems.length} stay{uniqueItems.length === 1 ? "" : "s"}</div>
                    </td>
                    {dayList.map((d) => {
                      const iso = toISODate(year, month, d);
                      const isToday = iso === todayISO;
                      const dow = new Date(year, month, d).getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      // Half-open interval so same-day turnover (A checkout == B checkin) isn't a conflict
                      const cellItems = uniqueItems.filter((x) => {
                        if (x.cancelled) return false;
                        const ci = x.b.check_in;
                        const co = x.b.check_out || ci;
                        if (ci === co) return iso === ci;
                        return iso >= ci && iso < co;
                      });
                      const conflict = cellItems.length > 1;
                      // Only render a bar in the START day cell of the booking to span full width
                      const startingHere = uniqueItems.filter((x) => x.startDay === d);
                      return (
                        <td
                          key={d}
                          className={`border-b border-r border-slate-200 p-0 relative min-w-[56px] align-top ${
                            isToday ? "bg-blue-50/40" : isWeekend && cellItems.length === 0 ? "bg-slate-50/60" : ""
                          }`}
                          style={{ height: `${rowH}px` }}
                        >
                          {isToday && (
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-900 pointer-events-none" />
                          )}
                          {/* Task chips (per property; render for every day within start_date..end_date) */}
                          <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5 z-10 pointer-events-auto">
                            {tasks
                              .filter((t) => {
                                if (t.property_booked !== prop) return false;
                                const s = t.start_date || t.scheduled_date;
                                const e = t.end_date || s;
                                return s && iso >= s && iso <= e;
                              })
                              .map((t) => (
                                <TaskChip
                                  key={t.id}
                                  task={t}
                                  onClick={(tk) => { setTaskInitial(tk); setTaskOpen(true); }}
                                />
                              ))}
                          </div>
                          <div className="relative w-full h-full">
                            {startingHere.map((x) => {
                              const span = Math.min(x.endDay, days) - x.startDay + 1;
                              const isConflict = !x.cancelled && conflict;
                              const laneIdx = laneInfo.bookingLane.get(x.b.id) ?? 0;
                              const color = x.cancelled
                                ? "#94A3B8"
                                : isConflict
                                ? "#DC2626"
                                : CHANNEL_COLORS[x.b.ota_or_direct] || "#1E3A8A";
                              return (
                                <HoverCard key={x.b.id} openDelay={120} closeDelay={80}>
                                  <HoverCardTrigger asChild>
                                    <div
                                      onClick={() => navigate(`/bookings/${x.b.id}`)}
                                      className={`absolute cursor-pointer rounded-sm text-white overflow-hidden hover:opacity-90 transition-opacity shadow-sm ${x.cancelled ? "line-through" : ""}`}
                                      style={{
                                        background: x.cancelled
                                          ? "repeating-linear-gradient(45deg,#94A3B8,#94A3B8 4px,#CBD5E1 4px,#CBD5E1 8px)"
                                          : color,
                                        top: `${laneIdx * 26 + 4}px`,
                                        height: "22px",
                                        left: "2px",
                                        width: `calc(${span * 56}px - 4px)`,
                                        zIndex: 5,
                                        opacity: x.cancelled ? 0.85 : 1,
                                      }}
                                      data-testid={`cal-booking-${x.b.id}`}
                                    >
                                      <div className="px-1.5 py-0.5 flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold leading-tight">
                                        {x.cancelled && <span className="text-[8px] uppercase tracking-wider bg-white/25 px-1 rounded-sm">Cxl</span>}
                                        <span className="truncate">{x.b.booked_by || "Guest"}</span>
                                        <span className="opacity-80 text-[10px] flex items-center gap-0.5 shrink-0">
                                          <Moon className="w-2.5 h-2.5" />{x.totalNights}
                                        </span>
                                      </div>
                                    </div>
                                  </HoverCardTrigger>
                                  <HoverCardContent className="w-72 rounded-sm p-4" data-testid={`cal-hover-${x.b.id}`}>
                                    <div className="flex items-center gap-2 pb-2 mb-2 border-b border-slate-200">
                                      <div className="w-2 h-2 rounded-sm" style={{ background: color }} />
                                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                                        {x.b.ota_or_direct || "Unassigned"}
                                      </span>
                                    </div>
                                    <div className="font-heading font-semibold text-slate-900 truncate">{x.b.booked_by || "Guest"}</div>
                                    <div className="text-xs text-slate-500 mt-0.5 truncate">{x.b.property_booked}</div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                      <div><div className="label-eyebrow text-[9px]">Check-in</div><div className="font-medium">{x.b.check_in || "—"}</div></div>
                                      <div><div className="label-eyebrow text-[9px]">Check-out</div><div className="font-medium">{x.b.check_out || "—"}</div></div>
                                      <div><div className="label-eyebrow text-[9px]">Nights</div><div className="font-medium">{x.totalNights}</div></div>
                                      <div><div className="label-eyebrow text-[9px]">Guests</div><div className="font-medium">{x.b.guests || 0}</div></div>
                                      <div><div className="label-eyebrow text-[9px]">Payout</div><div className="font-semibold text-blue-900">£{(x.b.host_payout || 0).toFixed(2)}</div></div>
                                      <div><div className="label-eyebrow text-[9px]">Payment</div><div className="font-medium">{x.b.payment_status || "—"}</div></div>
                                    </div>
                                    {x.b.reservation_code && (
                                      <div className="mt-3 pt-2 border-t border-slate-200 text-[10px] text-slate-500 font-mono">
                                        {x.b.reservation_code}
                                      </div>
                                    )}
                                    <button
                                      onClick={() => navigate(`/bookings/${x.b.id}`)}
                                      className="mt-3 w-full text-center text-xs font-semibold text-blue-900 hover:underline label-eyebrow"
                                    >
                                      Open booking →
                                    </button>
                                  </HoverCardContent>
                                </HoverCard>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap gap-3 sm:gap-4 items-center">
        <div className="label-eyebrow">Legend</div>
        {Object.entries(CHANNEL_COLORS).filter(([k]) => k !== "Unassigned").map(([name, color]) => (
          <div key={name} className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded-sm" style={{ background: color }} />
            <span className="text-slate-700">{name}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="w-3 h-3 rounded-sm" style={{ background: "#DC2626" }} />
          <span className="text-slate-700 font-semibold">Double-booking</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span
            className="w-3 h-3 rounded-sm"
            style={{ background: "repeating-linear-gradient(45deg,#94A3B8,#94A3B8 2px,#CBD5E1 2px,#CBD5E1 4px)" }}
          />
          <span className="text-slate-700">Cancelled (record only)</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="w-3 h-3 rounded-sm border" style={{ borderColor: "#0891B2", background: "white" }} />
          <span className="text-slate-700">Cleaning task</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="w-3 h-3 rounded-sm border" style={{ borderColor: "#B45309", background: "white" }} />
          <span className="text-slate-700">Maintenance task</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs ml-auto">
          <Users className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-500">Bar shows guest · <Moon className="w-3 h-3 inline mb-0.5" /> nights · hover for details</span>
        </div>
      </div>

      <PropertiesManager
        open={managerOpen}
        onOpenChange={setManagerOpen}
        onChanged={load}
      />
      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        initial={taskInitial}
        properties={properties}
        onSaved={load}
      />

      {/* iCal feeds (read-only, per-property × channel; separate from main calendar) */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6 space-y-4" data-testid="ical-feeds-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Rss className="w-3.5 h-3.5" /> iCal Sync (cross-check only)</div>
            <h3 className="font-heading text-xl font-medium mt-1 text-slate-900">External calendars — {company}</h3>
            <p className="text-xs text-slate-500 mt-1">
              Configure one iCal URL per property × channel (Airbnb, Booking.com, VRBO, Other OTA). Read-only view — never merged into the main calendar above.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={addFeedRow}
              className="rounded-sm border-slate-300 h-10"
              data-testid="ical-add-row"
            >
              <Plus className="w-4 h-4 mr-2" /> Add feed
            </Button>
            <Button
              onClick={() => syncFeeds(true)}
              disabled={icalLoading || feeds.length === 0}
              className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white h-10"
              data-testid="ical-sync"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${icalLoading ? "animate-spin" : ""}`} />
              {icalLoading ? "Syncing…" : "Save & Sync"}
            </Button>
          </div>
        </div>

        {feeds.length === 0 && (
          <div className="p-4 text-center text-xs text-slate-500 border border-dashed border-slate-300 rounded-sm">
            No iCal feeds configured. Click "Add feed" to paste an OTA URL for a specific property.
          </div>
        )}

        {feeds.length > 0 && (
          <div className="space-y-2">
            {feeds.map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[200px_140px_1fr_auto] gap-2 items-center" data-testid={`ical-row-${i}`}>
                <select
                  value={row.property}
                  onChange={(e) => updateFeedRow(i, "property", e.target.value)}
                  className="h-9 px-3 rounded-sm border border-slate-300 text-sm bg-white"
                  data-testid={`ical-row-${i}-property`}
                >
                  <option value="">Select property…</option>
                  {properties.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select
                  value={row.channel}
                  onChange={(e) => updateFeedRow(i, "channel", e.target.value)}
                  className="h-9 px-3 rounded-sm border border-slate-300 text-sm bg-white"
                  data-testid={`ical-row-${i}-channel`}
                >
                  {["Airbnb", "Booking.com", "VRBO", "Other OTA"].map((ch) => (
                    <option key={ch} value={ch}>{ch}</option>
                  ))}
                </select>
                <Input
                  value={row.url}
                  onChange={(e) => updateFeedRow(i, "url", e.target.value)}
                  placeholder="https://…/calendar.ics"
                  className="rounded-sm border-slate-300 h-9 text-xs font-mono"
                  data-testid={`ical-row-${i}-url`}
                />
                <button
                  onClick={() => removeFeedRow(i)}
                  className="text-slate-400 hover:text-rose-700 p-2"
                  data-testid={`ical-row-${i}-remove`}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {icalSavedAt && (
          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Auto-syncing every 60 s · last {icalSavedAt} · {icalEvents.length} event{icalEvents.length === 1 ? "" : "s"} across {new Set(icalEvents.map((e) => e.property)).size} propert{new Set(icalEvents.map((e) => e.property)).size === 1 ? "y" : "ies"}
          </div>
        )}

        {icalErrors.length > 0 && (
          <div className="text-xs text-rose-700 space-y-1">
            {icalErrors.map((e, i) => (
              <div key={i} data-testid={`ical-error-${i}`}>
                <strong>{e.property} · {e.channel}</strong> — {e.error}
              </div>
            ))}
          </div>
        )}

        {/* External calendar grid — mirrors main calendar layout but read-only */}
        {(icalEvents.length > 0 || feeds.some((f) => f.property && f.url)) && (
          <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="ical-grid">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-900">{MONTHS[month]} {year} · external OTA bookings</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                {icalEvents.filter((e) => {
                  const ms = toISODate(year, month, 1);
                  const me = toISODate(year, month, daysInMonth(year, month));
                  return e.start <= me && (e.end || e.start) >= ms;
                }).length} in view · {icalEvents.length} total
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" style={{ minWidth: `${240 + daysInMonth(year, month) * 56}px` }}>
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200">
                    <th className="sticky left-0 z-10 bg-slate-100 border-r border-slate-200 px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-slate-600 w-[240px] min-w-[240px]">
                      Property
                    </th>
                    {Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1).map((d) => {
                      const dow = new Date(year, month, d).getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      return (
                        <th
                          key={d}
                          className={`border-r border-slate-200 text-[10px] font-semibold text-slate-600 h-8 min-w-[56px] ${
                            isWeekend ? "bg-slate-50" : ""
                          }`}
                        >
                          <div className="text-slate-400">{DAY_LETTERS[dow]}</div>
                          <div>{d}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Set([
                    ...icalEvents.map((e) => e.property),
                    ...feeds.filter((f) => f.property).map((f) => f.property),
                  ])).sort().map((prop) => {
                    const days = daysInMonth(year, month);
                    const monthStartIso = toISODate(year, month, 1);
                    const monthEndIso = toISODate(year, month, days);
                    // Filter events for this property that overlap current month
                    const propEvents = icalEvents
                      .filter((e) => e.property === prop)
                      .filter((e) => e.start <= monthEndIso && (e.end || e.start) >= monthStartIso)
                      .sort((a, b) => a.start.localeCompare(b.start));
                    // Lane assignment for stacked bars (no overlap)
                    const laneEnds = [];
                    const eventLane = new Map();
                    propEvents.forEach((e, idx) => {
                      const key = e.uid + e.start + idx;
                      const s = e.start;
                      const eend = e.end || e.start;
                      let li = -1;
                      for (let i = 0; i < laneEnds.length; i++) {
                        if (laneEnds[i] <= s) { li = i; break; }
                      }
                      if (li === -1) { li = laneEnds.length; laneEnds.push(""); }
                      laneEnds[li] = eend;
                      eventLane.set(key, li);
                    });
                    const rowH = Math.max(44, laneEnds.length * 22 + 10);
                    return (
                      <tr key={prop} data-testid={`ical-grid-row-${prop}`} className="hover:bg-slate-50/60">
                        <td className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 px-3 py-1.5 font-medium truncate max-w-[240px]" style={{ height: `${rowH}px` }}>
                          <div className="text-sm text-slate-900 truncate">{prop}</div>
                          <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                            {propEvents.length} in view
                          </div>
                        </td>
                        {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                          const iso = toISODate(year, month, d);
                          const dow = new Date(year, month, d).getDay();
                          const isWeekend = dow === 0 || dow === 6;
                          // find events starting today (relative to month view)
                          const startingHere = propEvents.filter((e, idx) => {
                            const startInMonth = e.start < monthStartIso ? monthStartIso : e.start;
                            return startInMonth === iso;
                          });
                          return (
                            <td
                              key={d}
                              className={`border-b border-r border-slate-200 p-0 relative min-w-[56px] align-top ${
                                isWeekend ? "bg-slate-50/60" : ""
                              }`}
                              style={{ height: `${rowH}px` }}
                            >
                              <div className="relative w-full h-full">
                                {startingHere.map((ev, idx2) => {
                                  const key = ev.uid + ev.start + propEvents.indexOf(ev);
                                  const laneIdx = eventLane.get(key) ?? 0;
                                  const eend = ev.end || ev.start;
                                  const endClamped = eend > monthEndIso ? monthEndIso : eend;
                                  const startClamped = ev.start < monthStartIso ? monthStartIso : ev.start;
                                  const sd = parseInt(startClamped.slice(8, 10), 10);
                                  const edn = parseInt(endClamped.slice(8, 10), 10);
                                  const span = Math.max(1, edn - sd);
                                  const color = CHANNEL_COLORS[ev.channel] || "#64748B";
                                  return (
                                    <HoverCard key={key} openDelay={120} closeDelay={80}>
                                      <HoverCardTrigger asChild>
                                        <div
                                          className="absolute cursor-help rounded-sm text-white overflow-hidden shadow-sm hover:opacity-90 transition-opacity"
                                          style={{
                                            background: color,
                                            top: `${laneIdx * 22 + 4}px`,
                                            height: "18px",
                                            left: "2px",
                                            width: `calc(${span * 56}px - 4px)`,
                                            zIndex: 5,
                                          }}
                                          data-testid={`ical-event-bar-${key}`}
                                        >
                                          <div className="px-1.5 text-[10px] leading-[18px] truncate">
                                            {ev.summary || ev.channel}
                                          </div>
                                        </div>
                                      </HoverCardTrigger>
                                      <HoverCardContent side="top" align="start" className="w-80 rounded-sm shadow-xl">
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                                            <div className="label-eyebrow text-[10px]">{ev.channel}</div>
                                          </div>
                                          <div className="text-sm font-semibold text-slate-900 break-words">{ev.summary || "—"}</div>
                                          {ev.description && (
                                            <div className="text-xs text-slate-600 whitespace-pre-line break-words max-h-32 overflow-y-auto">
                                              {ev.description}
                                            </div>
                                          )}
                                          <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                                            <div><div className="label-eyebrow text-[9px]">Check-in</div><div className="font-medium tabular-nums">{ev.start}</div></div>
                                            <div><div className="label-eyebrow text-[9px]">Check-out</div><div className="font-medium tabular-nums">{ev.end}</div></div>
                                          </div>
                                          {ev.uid && (
                                            <div className="pt-2 border-t border-slate-200 text-[10px] text-slate-500 font-mono truncate">{ev.uid}</div>
                                          )}
                                        </div>
                                      </HoverCardContent>
                                    </HoverCard>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Full event details — collapsible list of all previous & upcoming events */}
        {icalEvents.length > 0 && (
          <details className="border border-slate-200 rounded-sm overflow-hidden" data-testid="ical-details-toggle">
            <summary className="px-3 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer text-sm font-semibold text-slate-900 hover:bg-slate-100">
              All external bookings ({icalEvents.length}) — previous &amp; upcoming
            </summary>
            <div className="space-y-3 p-3">
              {Array.from(new Set(icalEvents.map((e) => e.property))).sort().map((prop) => {
                const propEvents = icalEvents.filter((e) => e.property === prop);
                return (
                  <div key={prop} className="border border-slate-200 rounded-sm overflow-hidden">
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-900">{prop}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{propEvents.length} event{propEvents.length === 1 ? "" : "s"}</div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 font-semibold">Channel</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Summary</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Check-in</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Check-out</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propEvents.map((ev, idx) => (
                            <tr key={ev.uid + ev.start + idx} className="border-b border-slate-100 hover:bg-blue-50/40">
                              <td className="px-3 py-1.5">
                                <span className="inline-flex items-center gap-1.5 text-xs">
                                  <span className="w-2 h-2 rounded-full" style={{ background: CHANNEL_COLORS[ev.channel] || "#64748B" }} />
                                  {ev.channel}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 truncate max-w-[280px]" title={ev.description || ev.summary}>{ev.summary || "—"}</td>
                              <td className="px-3 py-1.5 tabular-nums">{ev.start}</td>
                              <td className="px-3 py-1.5 tabular-nums">{ev.end}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-4 sm:p-6 ${highlight ? "bg-rose-50 border-rose-300" : "bg-white border-slate-200"}`}
    >
      <div className="label-eyebrow">{label}</div>
      <div className={`font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-2 sm:mt-3 ${highlight ? "text-rose-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
